import type { UpcomingLaunch } from '@vtak/mod-space/client';
import type { LaunchFocus } from '@vtak/shared';

/** Sort orderings offered by the mission filters. */
export type LaunchSort = 'default' | 'provider' | 'name';

/** Active filter selections (empty string / empty list = "any"). */
export interface LaunchFilter {
  query: string;
  provider: string;
  orbit: string;
  sites: string[];
  rockets: string[];
  sort: LaunchSort;
}

/** The neutral filter: match everything, natural order. */
export const EMPTY_FILTER: LaunchFilter = {
  query: '',
  provider: '',
  orbit: '',
  sites: [],
  rockets: [],
  sort: 'default',
};

/**
 * Whether a launch's pad/location matches any focused site (empty = all).
 * Sites are data-derived location names, matched by case-insensitive
 * containment so a stored short name ("Cape Canaveral") matches the full
 * location string — no hardcoded alias table.
 */
function matchesSites(launch: UpcomingLaunch, sites: string[]): boolean {
  if (sites.length === 0) return true;
  const where = `${launch.location} ${launch.padName}`.toLowerCase();
  return sites.some((site) => where.includes(site.toLowerCase()));
}

/** Whether a launch's rocket matches any focused rocket (empty = all). */
function matchesRockets(launch: UpcomingLaunch, rockets: string[]): boolean {
  if (rockets.length === 0) return true;
  const rocket = launch.rocket.toLowerCase();
  return rockets.some((focused) => rocket.includes(focused.toLowerCase()));
}

/** Overlay a unit's saved launch focus onto a filter. */
export function withFocus(filter: LaunchFilter, focus: LaunchFocus | undefined): LaunchFilter {
  if (!focus) return filter;
  return { ...filter, sites: focus.sites, rockets: focus.rockets };
}

/** Fields a free-text query searches across. */
function haystack(launch: UpcomingLaunch): string {
  return [launch.name, launch.mission, launch.rocket, launch.provider, launch.location, launch.program]
    .join(' ')
    .toLowerCase();
}

/** Whether a launch matches the free-text query (case-insensitive substring). */
function matchesQuery(launch: UpcomingLaunch, query: string): boolean {
  return query === '' || haystack(launch).includes(query.toLowerCase());
}

/** Whether a launch matches the provider/orbit/site/rocket selections. */
function matchesFacets(launch: UpcomingLaunch, filter: LaunchFilter): boolean {
  return (
    (filter.provider === '' || launch.provider === filter.provider) &&
    (filter.orbit === '' || launch.orbit === filter.orbit) &&
    matchesSites(launch, filter.sites) &&
    matchesRockets(launch, filter.rockets)
  );
}

/** Comparators for each sort ordering. */
const COMPARATORS: Readonly<Record<LaunchSort, (a: UpcomingLaunch, b: UpcomingLaunch) => number>> = {
  default: () => 0,
  provider: (a, b) => a.provider.localeCompare(b.provider),
  name: (a, b) => (a.mission || a.name).localeCompare(b.mission || b.name),
};

/** Apply text + facet filters and the chosen sort, preserving input order for ties. */
export function filterLaunches(launches: UpcomingLaunch[], filter: LaunchFilter): UpcomingLaunch[] {
  const matched = launches.filter((launch) => matchesQuery(launch, filter.query) && matchesFacets(launch, filter));
  return filter.sort === 'default' ? matched : [...matched].sort(COMPARATORS[filter.sort]);
}

/** Sorted, de-duplicated non-empty values of one launch field (for dropdowns). */
export function distinctValues(launches: UpcomingLaunch[], pick: (launch: UpcomingLaunch) => string): string[] {
  const values = new Set<string>();
  for (const launch of launches) {
    const value = pick(launch);
    if (value) values.add(value);
  }
  return [...values].sort((a, b) => a.localeCompare(b));
}
